/**
 * ============================================
 * QUERY CLIENT - Configuración de TanStack Query
 * ============================================
 *
 * Este archivo configura el QueryClient que maneja:
 * - Cache de todas las queries
 * - Configuración global de comportamiento
 * - Tiempos de cache y revalidación
 *
 * Conceptos clave:
 * - staleTime: Tiempo que los datos son "frescos" (no se refetch)
 * - gcTime: Tiempo que los datos permanecen en cache sin uso
 * - retry: Reintentos automáticos en caso de error
 */

import { QueryClient } from "@tanstack/react-query";

/**
 * QueryClient configurado para la aplicación LIMS
 *
 * Configuración elegida:
 * - staleTime: 5 min - Los datos de laboratorio no cambian muy frecuentemente
 * - gcTime: 10 min - Mantener cache un poco más para navegación rápida
 * - retry: 1 - Solo 1 reintento para no saturar el servidor
 * - refetchOnWindowFocus: false - Evitar refetch innecesarios
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Tiempo que los datos se consideran "frescos"
      // Durante este tiempo, no se hace refetch aunque se monte un nuevo componente
      staleTime: 5 * 60 * 1000, // 5 minutos

      // Tiempo que los datos permanecen en cache después de que
      // ningún componente los está usando (garbage collection time)
      gcTime: 10 * 60 * 1000, // 10 minutos

      // Número de reintentos automáticos en caso de error
      // 1 = intento original + 1 reintento
      retry: 1,

      // No refetch automático cuando el usuario vuelve a la pestaña
      // Esto evita requests innecesarios y mejora la experiencia
      refetchOnWindowFocus: false,

      // No refetch cuando el componente se monta si los datos están en cache
      refetchOnMount: false,

      // Refetch cuando se reconecta a internet
      refetchOnReconnect: true,
    },
    mutations: {
      // Las mutaciones (POST, PUT, DELETE) no reintentan por defecto
      // porque podrían causar duplicados
      retry: 0,
    },
  },
});

/**
 * Query Keys - Constantes para identificar queries en el cache
 *
 * Usar constantes evita errores de typo y facilita la invalidación
 *
 * Ejemplo de uso:
 * - useQuery({ queryKey: queryKeys.sucursales })
 * - queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
 */
export const queryKeys = {
  // Sucursales
  sucursales: ["sucursales"] as const,

  // Usuarios
  users: {
    all: ["users"] as const,
    list: (filters: object) => ["users", "list", filters] as const,
    detail: (id: number) => ["users", "detail", id] as const,
  },

  // Pacientes
  pacientes: {
    all: ["pacientes"] as const,
    list: (filters: object) => ["pacientes", "list", filters] as const,
    detail: (id: number) => ["pacientes", "detail", id] as const,
    search: (query: string) => ["pacientes", "search", query] as const,
  },

  // Órdenes
  ordenes: {
    all: ["ordenes"] as const,
    list: (filters: object) => ["ordenes", "list", filters] as const,
    detail: (id: number) => ["ordenes", "detail", id] as const,
    pagos: (id: number) => ["ordenes", "pagos", id] as const,
  },

  // Orden Exámenes (vista plana)
  ordenExamenes: {
    all: ["ordenExamenes"] as const,
    list: (filters: object) => ["ordenExamenes", "list", filters] as const,
  },

  // Exámenes Tipo
  examenesTipo: {
    all: ["examenesTipo"] as const,
    list: (filters: object) => ["examenesTipo", "list", filters] as const,
    detail: (id: number) => ["examenesTipo", "detail", id] as const,
    search: (query: string) => ["examenesTipo", "search", query] as const,
  },

  // Perfiles de Examen
  perfiles: {
    all: ["perfiles"] as const,
    list: (filters: object) => ["perfiles", "list", filters] as const,
    detail: (id: number) => ["perfiles", "detail", id] as const,
    search: (query: string) => ["perfiles", "search", query] as const,
  },

  // Valores de Referencia
  valoresReferencia: {
    all: ["valoresReferencia"] as const,
    list: (filters: object) => ["valoresReferencia", "list", filters] as const,
  },

  // Cotizaciones
  cotizaciones: {
    all: ["cotizaciones"] as const,
    list: (filters: object) => ["cotizaciones", "list", filters] as const,
    detail: (id: number) => ["cotizaciones", "detail", id] as const,
  },

  // Turnos
  turnos: {
    all: ["turnos"] as const,
    panel: (sucursalId: number, fecha?: string) => ["turnos", "panel", sucursalId, fecha] as const,
    detail: (id: number) => ["turnos", "detail", id] as const,
    estadisticas: (sucursalId: number, fechaInicio?: string, fechaFin?: string) =>
      ["turnos", "estadisticas", sucursalId, fechaInicio, fechaFin] as const,
    tiempoEspera: (sucursalId: number, prioridad?: string) =>
      ["turnos", "tiempo-espera", sucursalId, prioridad] as const,
    puntosAtencion: (sucursalId?: number) => ["turnos", "puntos-atencion", sucursalId] as const,
    pantallaPublica: (sucursalCodigo: string) =>
      ["turnos", "pantalla-publica", sucursalCodigo] as const,
  },

  // Médicos Remitentes
  medicosRemitentes: {
    all: ["medicos-remitentes"] as const,
    list: (filters: object) => ["medicos-remitentes", "list", filters] as const,
    detail: (id: number) => ["medicos-remitentes", "detail", id] as const,
    search: (query: string) => ["medicos-remitentes", "search", query] as const,
  },

  // Clientes
  clientes: {
    all: ["clientes"] as const,
    list: (filters: object) => ["clientes", "list", filters] as const,
    detail: (id: number) => ["clientes", "detail", id] as const,
    search: (query: string) => ["clientes", "search", query] as const,
  },

  // Facturación
  facturas: {
    all: ["facturas"] as const,
    list: (filters: object) => ["facturas", "list", filters] as const,
    detail: (id: number) => ["facturas", "detail", id] as const,
    ordenesDisponibles: (pacienteId: number, sucursalId: number) =>
      ["facturas", "ordenes-disponibles", pacienteId, sucursalId] as const,
    ordenesDisponiblesPorCliente: (clienteId: number, sucursalId: number) =>
      ["facturas", "ordenes-disponibles-cliente", clienteId, sucursalId] as const,
    ordenPorNumero: (numero: string, sucursalId: number) =>
      ["facturas", "orden-por-numero", numero, sucursalId] as const,
  },

  metodosPago: {
    all: ["metodosPago"] as const,
    list: (filters: object) => ["metodosPago", "list", filters] as const,
  },

  // CAI
  cais: {
    all: ["cais"] as const,
    list: (filters: object) => ["cais", "list", filters] as const,
    detail: (id: number) => ["cais", "detail", id] as const,
  },

  // Listas de Precios
  listasPrecios: {
    all: ["listasPrecios"] as const,
    list: (filters: object) => ["listasPrecios", "list", filters] as const,
    detail: (id: number) => ["listasPrecios", "detail", id] as const,
  },

  // Precios Examen
  preciosExamen: {
    all: ["preciosExamen"] as const,
    list: (filters: object) => ["preciosExamen", "list", filters] as const,
  },

  // Búsqueda Avanzada
  busquedaAvanzada: {
    all: ["busquedaAvanzada"] as const,
    search: (params: object | null) => ["busquedaAvanzada", "search", params] as const,
  },

  // Muestras
  muestras: {
    all: ["muestras"] as const,
    cola: (sucursalId: number, tipoServicio?: string) =>
      ["muestras", "cola", sucursalId, tipoServicio] as const,
    detail: (id: number) => ["muestras", "detail", id] as const,
    perfilClinico: (atencionId: number) => ["muestras", "perfil-clinico", atencionId] as const,
    validaciones: (atencionId: number) => ["muestras", "validaciones", atencionId] as const,
    muestras: (atencionId: number) => ["muestras", "muestras", atencionId] as const,
    completitud: (atencionId: number) => ["muestras", "completitud", atencionId] as const,
    tiposContenedor: ["muestras", "tipos-contenedor"] as const,
    auditoria: (filters: object) => ["muestras", "auditoria", filters] as const,
    estadisticas: (params: object) => ["muestras", "estadisticas", params] as const,
  },

  // Roles
  roles: ["roles"] as const,

  // Auth
  auth: {
    me: ["auth", "me"] as const,
  },

  // Procesamiento
  procesamiento: {
    all: ["procesamiento"] as const,
    cola: (filters: object) => ["procesamiento", "cola", filters] as const,
    contadores: (sucursalId: number) => ["procesamiento", "contadores", sucursalId] as const,
    resultado: (ordenExamenId: number) => ["procesamiento", "resultado", ordenExamenId] as const,
  },
} as const;
